import { remove } from 'lodash';
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import { bind, deepStringFind, Modal, ModalHandle, StatusCard } from 'Mri.Web.Common';
import * as React from 'react';
import { Input } from 'reactstrap';
import { TicketModal } from '../../Core/TicketModal';
import { ActiveDisplay } from '../ActiveDisplay';
import { DisplayEventGroup, DisplayEventsList } from './DisplayEventsList';

export interface IDisplayEventsCardProps {
    readonly display: ActiveDisplay;
    readonly loading: boolean;
}

@observer
export class DisplayEventsCard extends React.Component<IDisplayEventsCardProps> {
    public render() {
        return (
            <StatusCard loading={this.props.loading} title={this.renderTitle()}>
                <Input
                    type="text"
                    placeholder="Search Events..."
                    value={this.searchValue}
                    onChange={this.onSearchInputChange}
                    className="rounded-0 ticket-search"
                />

                <div className="scroll-container mt-3">
                    <div className="scroll-card">
                        {this.checkedEvents && this.displayEvents && (
                            <DisplayEventsList
                                displayEvents={this.displayEvents}
                                renderRow={this.renderRow}
                                serialNumber={this.props.display.serialNumber}
                                loading={this.props.loading}
                            />
                        )}
                    </div>
                </div>

                {this.props.display && (
                    <TicketModal
                        modalHandle={this.ticketModalHandle}
                        serialNumber={this.props.display.serialNumber}
                        displayEvents={this.checkedEvents}
                        display={this.props.display}
                    />
                )}

                <Modal
                    className="modal-sm"
                    title="Confirm Ticket"
                    modalHandle={this.confirmHandle}
                    btnClass="w-50"
                    footerClass="d-flex justify-content-center"
                >
                    <div className="text-center">
                        One or more events already has a ticket submitted or is being ignored. Would you still like to
                        submit a ticket?
                    </div>
                </Modal>
            </StatusCard>
        );
    }

    @bind
    private renderTitle() {
        return (
            <div className="d-flex">
                {this.displayEvents?.length} {this.displayEvents?.length == 1 ? 'Event' : 'Events'}
                {!!this.props.display.activeDisplayEvents?.length && (
                    <a onClick={this.openTicketModal} className="btn btn-primary btn-sm ms-auto">
                        {this.checkedEvents.length > 1
                            ? 'Report Downtime Incidents'
                            : this.checkedEvents.length == 1
                                ? 'Report Downtime Incident'
                                : 'Submit Ticket'}
                    </a>
                )}
            </div>
        );
    }

    @bind
    @action
    private onSearchInputChange(event: React.FormEvent<HTMLInputElement>) {
        const target = event.target as HTMLInputElement;
        this.searchValue = target.value;
    }

    @bind
    private renderRow(displayEvent: Mri.DisplayEvents.DisplayEvent, group: DisplayEventGroup, isHead: boolean) {
        return (
            <div className="display-events-checkbox" style={{ backgroundColor: 'inherit' }}>
                <input
                    type="checkbox"
                    checked={this.isChecked(displayEvent)}
                    onChange={event => this.onCheck(event, displayEvent)}
                />
            </div>
        );
    }

    @bind
    @action
    private onCheck(event: React.FormEvent<HTMLInputElement>, displayEvent: Mri.DisplayEvents.DisplayEvent) {
        const target = event.target as HTMLInputElement;

        remove(this.checkedEvents, de => de.displayEventGuid == displayEvent.displayEventGuid);

        if (target.checked) {
            this.checkedEvents.push(displayEvent);
        }
    }

    @bind
    private isChecked(displayEvent: Mri.DisplayEvents.DisplayEvent) {
        return !!this.checkedEvents.find(de => de.displayEventGuid == displayEvent.displayEventGuid);
    }

    @bind
    private async openTicketModal(event?: React.MouseEvent<HTMLAnchorElement>) {
        if (this.checkedEvents.find(d => d.displayEventStatus != Mri.DisplayEvents.DisplayEventStatus.New)) {
            this.confirmHandle.open();

            if (await this.confirmHandle.confirm()) {
                this.ticketModalHandle.open();
            }
        } else {
            this.ticketModalHandle.open();
        }
    }

    @computed
    private get displayEvents() {
        let displayEvents = this.props.display.activeDisplayEvents;
        if (this.searchValue && displayEvents)
            displayEvents = displayEvents.filter((o: any) => deepStringFind(o, this.searchValue));
        return displayEvents;
    }

    private ticketModalHandle = new ModalHandle();

    @observable
    private confirmHandle = new ModalHandle();

    @observable
    private searchValue: string = '';

    @observable
    private checkedEvents: Mri.DisplayEvents.DisplayEvent[] = [];
}
